import React, { useState, useEffect, useMemo } from "react";
import ExifReader from "exifreader";
import get from "lodash/get";
import PreviewMap from "./PreviewMap";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import PreviewImages from "./PreviewImages";
import { InputGroup, Spinner } from "react-bootstrap";
import { has } from "lodash";

const UploadPreview = ({ images, latField, lonField, remove, setRemove }) => {
  const [show, setShow] = useState(false);
  const [index, setIndex] = useState(0);
  const [centroid, setCentroid] = useState([0, 0]);
  const [currentPhoto, setCurrentPhoto] = useState();
  const [currentImage, setCurrentImage] = useState();
  const [removedPoints, setRemovedPoints] = useState();
  const [points, setPoints] = useState();

  useMemo(() => {
    const getCoords = async () => {
      const currentPoints = [];
      for (let i = 0; i < images.length; i++) {
        const buffer = await images[i].arrayBuffer();
        const tags = await ExifReader.load(buffer, {
          expanded: true,
          includeUnknown: true,
          async: true,
        });

        if (!tags) {
          return;
        }

        if (!has(tags, latField) || !has(tags, lonField)) {
          return;
        }

        let gpsLatitude = get(tags, latField);
        let gpsLongitude = get(tags, lonField);

        if (
          isNaN(gpsLatitude) ||
          isNaN(gpsLongitude) ||
          !gpsLatitude ||
          !gpsLongitude
        ) {
          return;
        }
        currentPoints.push({
          geometry: {
            type: "point",
            longitude: gpsLongitude,
            latitude: gpsLatitude,
          },
          symbol: {
            type: "simple-marker",
            color: [100, 100, 100],
            size: "12px",
            outline: {
              color: [255, 255, 255],
              width: 1,
            },
          },
          attributes: {
            id: i,
          },
        });
      }
      setPoints(currentPoints);
    };
    if (images.length) getCoords();
  }, [images, latField, lonField]);

  useEffect(() => {
    const rPoints = [];
    remove.forEach((r) => {
      const geometry = points?.[r]?.geometry;
      const symbol = {
        type: "simple-marker",
        color: [200, 10, 10],
        size: "14px",
        outline: {
          color: [255, 255, 255],
          width: 1,
        },
      };
      const attributes = {
        id: r,
      };

      rPoints.push({
        geometry: geometry,
        attributes: attributes,
        symbol: symbol,
      });
    });
    setRemovedPoints(rPoints);
  }, [remove, points]);

  useEffect(() => {
    const geometry = points?.[index]?.geometry;
    const symbol = {
      type: "simple-marker",
      color: [248, 153, 28],
      size: "16px",
      outline: {
        color: [255, 255, 255],
        width: 1,
      },
    };
    setCurrentPhoto({ geometry: geometry, symbol: symbol });
    setCentroid([
      points?.[index]?.geometry?.longitude,
      points?.[index]?.geometry?.latitude,
    ]);
  }, [points?.length, points, index]);

  useEffect(() => {
    setCurrentImage(images[index]);
  }, [images, index]);

  const handleOpen = () => {
    setCurrentImage(images[index]);
    setShow(true);
  };

  const handleClose = () => {
    setIndex(0);
    setShow(false);
  };

  const incrementIndex = () => {
    setIndex((prevValue) =>
      prevValue <= images.length ? (prevValue += 1) : prevValue
    );
    setCurrentImage(images[index]);
  };

  const decrementIndex = () => {
    setIndex((prevValue) => (prevValue > 0 ? (prevValue -= 1) : prevValue));
    setCurrentImage(images[index]);
  };

  const handleRemove = () => {
    if (!remove.has(index)) {
      setRemove((prev) => {
        const newSet = new Set(prev);
        newSet.add(index);
        return newSet;
      });
    } else {
      setRemove((prev) => {
        prev.delete(index);
        return new Set(prev);
      });
    }
  };

  return (
    <>
      <Modal size="xl" centered show={show} onHide={handleClose}>
        <Modal.Header closeButton>Preview</Modal.Header>
        <Modal.Body>
          <Row>
            <Col className="mx-0 px-1" sm={6}>
              {points?.length ? (
                <PreviewMap
                  points={points}
                  centroid={centroid}
                  currentPhoto={currentPhoto}
                  index={index}
                  setIndex={setIndex}
                  removedPoints={removedPoints}
                />
              ) : (
                <Spinner />
              )}
            </Col>
            <Col
              sm={6}
              className="mx-0 px-1"
              style={{
                height: "800px",
                maxHeight: "800px",
                overflowY: "hidden",
              }}
            >
              <PreviewImages image={currentImage} index={index} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Row className="m-auto d-flex justify-content-around align-items-center">
            <InputGroup className="d-flex">
              <Button
                style={{ color: "white" }}
                title="Previous Image"
                disabled={index <= 0}
                onClick={decrementIndex}
              >
                &lt;
              </Button>
              <InputGroup.Text>
                {index + 1} / {images?.length}
              </InputGroup.Text>
              <Button
                style={{ color: "white" }}
                title="Next Image"
                disabled={images?.length <= index}
                onClick={incrementIndex}
              >
                &gt;
              </Button>
              <InputGroup.Text>
                <Form.Check
                  label={"Keep"}
                  id={`keep-image-${index}`}
                  checked={!remove.has(index)}
                  onChange={handleRemove}
                />
              </InputGroup.Text>
            </InputGroup>
          </Row>
        </Modal.Footer>
      </Modal>
      <Button
        size="sm"
        variant="info"
        onClick={handleOpen}
        style={{ color: "white" }}
        disabled={images.length < 1}
      >
        Preview
      </Button>
    </>
  );
};

export default UploadPreview;
